import React, { useEffect, useState } from "react";
import GuestForm from "../components/forms/GuestForm";
import ThankYouMessage from "../components/ui-custom/ThankYouMessage";
import { supabase } from "@/integrations/supabase/client";
import { Mail, Phone, Clock } from "lucide-react";

interface TrackingIDs {
  fbPixelId: string | null;
  gaId: string | null;
}

// Extended window interface with proper Facebook Pixel typing
declare global {
  interface Window {
    fbq: any; // Using any for fbq since it has many properties and methods
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

const Index = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [trackingIds, setTrackingIds] = useState<TrackingIDs>({
    fbPixelId: null,
    gaId: null,
  });

  useEffect(() => {
    const fetchTrackingIDs = async () => {
      try {
        const { data, error } = await supabase
          .from('site_settings')
          .select('fb_pixel_id, ga_id')
          .limit(1)
          .single();
        
        if (error) {
          console.error('Error fetching tracking IDs:', error);
          return;
        }
        
        if (data) {
          setTrackingIds({
            fbPixelId: data.fb_pixel_id,
            gaId: data.ga_id,
          });
        }
      } catch (error) {
        console.error('Unexpected error fetching tracking IDs:', error);
      }
    };
    
    fetchTrackingIDs();
  }, []);
  
  useEffect(() => {
    // Initialize Facebook Pixel
    if (trackingIds.fbPixelId) {
      const initFacebookPixel = () => {
        // Define fbq function with all necessary properties
        window.fbq = function(...args: any[]) {
          // @ts-ignore
          window.fbq.callMethod ? window.fbq.callMethod.apply(window.fbq, arguments) : window.fbq.queue.push(arguments);
        };
        
        // Initialize fbq properties
        window.fbq.queue = [];
        window.fbq.callMethod = null;
        window.fbq.loaded = true;
        window.fbq.version = '2.0';
        
        // Load the pixel script
        const script = document.createElement('script');
        script.async = true;
        script.src = 'https://connect.facebook.net/en_US/fbevents.js';
        document.head.appendChild(script);
        
        // Initialize with the Pixel ID
        window.fbq('init', trackingIds.fbPixelId);
        window.fbq('track', 'PageView');
      };

      // If fbq is not already defined, initialize it
      if (typeof window !== 'undefined' && !window.fbq) {
        initFacebookPixel();
      }
    }
  }, [trackingIds.fbPixelId]);
  
  useEffect(() => {
    // Initialize Google Analytics
    if (trackingIds.gaId) {
      const initGoogleAnalytics = () => {
        // Load the GA script
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${trackingIds.gaId}`;
        document.head.appendChild(script);

        window.dataLayer = window.dataLayer || [];
        function gtag(...args: any[]) {
          window.dataLayer.push(arguments);
        }
        window.gtag = gtag;
        gtag('js', new Date());
        gtag('config', trackingIds.gaId);
      };

      // If gtag is not already defined, initialize it
      if (typeof window !== 'undefined' && typeof window.gtag === 'undefined') {
        initGoogleAnalytics();
      }
    }
  }, [trackingIds.gaId]);

  const handleSubmitSuccess = () => {
    // Track form submission event
    if (trackingIds.fbPixelId && window.fbq) {
      window.fbq('track', 'Lead');
    }
    
    if (trackingIds.gaId && window.gtag) {
      window.gtag('event', 'generate_lead', {
        'event_category': 'engagement',
        'event_label': 'form_submission'
      });
    }
    
    setIsSubmitted(true);
    
    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow">
        <div className="container mx-auto px-4 py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <img 
                src="https://diathermiki.gr/assets/img/diathermiki-square.jpg" 
                alt="Diathermiki Logo" 
                className="h-16 w-16 object-contain"
              />
              <div className="text-left">
                <h1 className="text-xl font-bold text-primary">Διαθερμική Α.Ε.</h1>
                <p className="text-sm text-gray-600">Το μεγαλύτερο δίκτυο Ενεργειακών, τεχνικών & καταστημάτων στην Ελλάδα</p>
              </div>
            </div>
          </div>
        </div>
      </header>
      
      <main className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto">
          {isSubmitted ? (
            <ThankYouMessage />
          ) : (
            <div className="bg-white shadow-lg rounded-lg overflow-hidden">
              <div className="bg-gradient-to-r from-blue-600 to-blue-800 px-6 py-4">
                <h1 className="text-2xl font-bold text-white">Φόρμα Ενδιαφέροντος</h1>
                <p className="text-blue-100 mt-1">
                  Συμπληρώστε τα στοιχεία σας για να επικοινωνήσουμε μαζί σας
                </p>
              </div>
              
              <div className="p-6">
                <GuestForm onSubmitSuccess={handleSubmitSuccess} />
              </div>
            </div>
          )}
        </div>
      </main>
      
      <footer className="bg-gray-800 text-white py-10 mt-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <img 
                  src="https://diathermiki.gr/assets/img/diathermiki-square.jpg" 
                  alt="Διαθερμική Logo" 
                  className="h-12 w-12 mr-3 rounded"
                />
                <h3 className="text-xl font-bold">Διαθερμική Α.Ε.</h3>
              </div>
              <p className="text-gray-300 mb-4">
                Το μεγαλύτερο δίκτυο Ενεργειακών, τεχνικών & καταστημάτων στην Ελλάδα.
              </p>
              <div className="flex space-x-4">
                <a href="#" className="text-white hover:text-blue-300 transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-facebook"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path></svg>
                </a>
                <a href="#" className="text-white hover:text-blue-300 transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-linkedin"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path><rect width="4" height="12" x="2" y="9"></rect><circle cx="4" cy="4" r="2"></circle></svg>
                </a>
                <a href="#" className="text-white hover:text-blue-300 transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-instagram"><rect width="20" height="20" x="2" y="2" rx="5" ry="5"></rect><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path><line x1="17.5" x2="17.51" y1="6.5" y2="6.5"></line></svg>
                </a>
              </div>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Επικοινωνία</h4>
              <div className="space-y-3 text-gray-300">
                <p className="flex items-center">
                  <Phone className="h-5 w-5 mr-2 text-blue-300" />
                  <a href="tel:+302310535975" className="hover:text-white">+30 2310 535975</a>
                </p>
                <p className="flex items-center">
                  <Phone className="h-5 w-5 mr-2 text-blue-300" />
                  <a href="tel:+302310536696" className="hover:text-white">+30 2310 536696</a>
                </p>
                <p className="flex items-center">
                  <Mail className="h-5 w-5 mr-2 text-blue-300" />
                  <a href="mailto:info@diathermiki.gr" className="hover:text-white">info@diathermiki.gr</a>
                </p>
              </div>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Ωράριο Καταστημάτων</h4>
              <div className="space-y-2 text-gray-300">
                <p className="flex items-start">
                  <Clock className="h-5 w-5 mr-2 text-blue-300 mt-1 flex-shrink-0" />
                  <span>Για την εξυπηρέτησή σας, παρακαλούμε επικοινωνήστε με το κατάστημα της περιοχής σας, ώστε να ενημερωθείτε για το ωράριο λειτουργίας του.</span>
                </p>
              </div>
              <div className="mt-4">
                <a href="#" className="text-blue-300 hover:text-white transition-colors">Πολιτική Προστασίας Προσωπικών Δεδομένων</a>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-700 mt-8 pt-6 text-center text-gray-400">
            <p>&copy; {new Date().getFullYear()} Διαθερμική Α.Ε. Με επιφύλαξη παντός δικαιώματος.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
